import * as Yup from 'yup';

import { validatePhone } from '../../../../helpers/formFieldValidations';
import { US_STATES } from '../../../../constants/formOptions';

export const borrowerFields = [
  {
    label: 'Type',
    placeholder: 'Select type',
    name: 'type',
    type: 'select',
    options: [
      { value: 'Individual', label: 'Individual' },
      { value: 'Entity', label: 'Entity' },
    ],
    validation: Yup.string(),
  },
  {
    label: 'First Name',
    placeholder: 'Type first name',
    name: 'firstName',
    type: 'text',
    validation: Yup.string().required('This field is required'),
  },
  {
    label: 'Last Name',
    placeholder: 'Type last name',
    name: 'lastName',
    type: 'text',
    validation: Yup.string().required('This field is required'),
  },
  {
    label: 'Email',
    placeholder: 'Type email',
    name: 'email',
    type: 'text',
    validation: Yup.string().email('Invalid email'),
  },
  {
    label: 'Phone',
    placeholder: 'Type phone',
    name: 'phone',
    type: 'phone',
    validation: validatePhone,
  },
  {
    label: 'Date of Birth',
    placeholder: 'Select date of birth',
    name: 'dateOfBirth',
    type: 'date',
    validation: Yup.date(),
  },
  {
    label: 'TIN',
    placeholder: 'Type TIN',
    name: 'tin',
    type: (values) => {
      if (values.type === 'Entity') {
        return 'ein';
      } else if (values.type === 'Individual') {
        return 'ssn';
      } else {
        return 'text';
      }
    },
    validation: Yup.string().test(
      'idValueFormat',
      'Invalid format for selected ID type',
      function (value) {
        const { type } = this.parent;

        if (!value) {
          return true;
        }

        if (type === 'Individual') {
          return /^\d{3}-?\d{2}-?\d{4}$/.test(value);
        } else if (type === 'Entity') {
          return /^\d{2}-\d{7}$/.test(value);
        }

        return true;
      }
    ),
  },
  {
    label: 'TIN Verified',
    name: 'isTinVerified',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Dependents',
    placeholder: 'Type dependents',
    name: 'dependents',
    type: 'text',
    validation: Yup.string()
      .typeError('This field must be a number')
      .test(
        'is-comma-separated-integers',
        'This field must be a comma separated list of integers',
        (value) => {
          if (!value) {
            return true;
          }

          const parts = value.split(',').map((item) => item.trim());

          return parts.every((part) => /^-?\d+$/.test(part));
        }
      ),
  },
  {
    label: 'Dependent Count',
    placeholder: 'Type dependent count',
    name: 'dependentCount',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
  {
    label: 'Citizenship',
    placeholder: 'Select citizenship',
    name: 'citizenship',
    type: 'select',
    options: [
      { value: 'U.S. Citizen', label: 'U.S. Citizen' },
      { value: 'Permanent Resident Alien', label: 'Permanent Resident Alien' },
      {
        value: 'Non-Permanent Resident Alien',
        label: 'Non-Permanent Resident Alien',
      },
    ],
    validation: Yup.string(),
  },
  {
    label: 'Marital Status',
    placeholder: 'Select marital status',
    name: 'maritalStatus',
    type: 'select',
    options: [
      { value: 'Married', label: 'Married' },
      { value: 'Separated', label: 'Separated' },
      {
        value: 'Unmarried',
        label: 'Unmarried',
      },
    ],
    validation: Yup.string(),
  },
  {
    label: 'Language',
    placeholder: 'Select language',
    name: 'language',
    type: 'select',
    options: [
      { value: 'English', label: 'English' },
      { value: 'Spanish', label: 'Spanish' },
    ],
    validation: Yup.string(),
  },
  {
    label: 'Gender',
    placeholder: 'Select gender',
    name: 'gender',
    type: 'select',
    options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
    ],
    validation: Yup.string(),
    fullWidth: true,
  },
  {
    label: 'I do not wish to provide this information',
    name: 'decSexNo',
    type: 'checkbox',
    validation: Yup.boolean(),
    fullWidth: true,
  },
  {
    label: 'Non-Borrower Spouse Name',
    placeholder: 'Type non-borrower spouse name',
    name: 'nonBorrowerSpouseName',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'Spouse Photo ID',
    name: 'hasSpousePhotoId',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Phone Home',
    placeholder: 'Type home phone',
    name: 'phoneHome',
    type: 'phone',
    validation: validatePhone,
  },
  {
    label: 'Phone Work',
    placeholder: 'Type work phone',
    name: 'phoneWork',
    type: 'phone',
    validation: validatePhone,
  },
];

export const addressFields = [
  {
    label: 'Address Line 1',
    placeholder: 'Type address line 1',
    name: 'addressLine1',
    type: 'text',
    validation: Yup.string(),
    fullWidth: true,
  },
  {
    label: 'Address Line 2',
    placeholder: 'Type address line 2',
    name: 'addressLine2',
    type: 'text',
    validation: Yup.string(),
    fullWidth: true,
  },
  {
    label: 'City',
    placeholder: 'Type city',
    name: 'city',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'State',
    placeholder: 'Select state',
    name: 'state',
    type: 'select',
    options: US_STATES,
    validation: Yup.string(),
  },
  {
    label: 'ZIP',
    placeholder: 'Type ZIP',
    name: 'zip',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'Time At Address',
    placeholder: 'Type time at address',
    name: 'timeAtAddress',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
  {
    label: 'Own or Rent',
    placeholder: 'Select own or rent',
    name: 'ownOrRent',
    type: 'select',
    options: [
      { value: 'Rent', label: 'Rent' },
      { value: 'Own', label: 'Own' },
    ],
    validtation: Yup.string(),
  },
  {
    label: 'Rent Amount',
    placeholder: 'Type rent amount',
    name: 'rentAmount',
    type: 'currency',
    validation: Yup.string(),
  },
  {
    label: 'Landlord Name',
    placeholder: 'Type landlord name',
    name: 'landlordName',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'Landlord Phone',
    placeholder: 'Type landlord phone',
    name: 'landlordPhone',
    type: 'phone',
    validation: validatePhone,
  },
  {
    label: 'Landlord Email',
    placeholder: 'Landlord email',
    name: 'landlordEmail',
    type: 'text',
    validation: Yup.string().email('Invalid email'),
  },
  {
    label: 'VOR',
    name: 'hasVor',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Previous Address Line 1',
    placeholder: 'Type previous address line 1',
    name: 'previousAddressLine1',
    type: 'text',
    validation: Yup.string(),
    fullWidth: true,
  },
  {
    label: 'Previous Address Line 2',
    placeholder: 'Type previous address line 2',
    name: 'previousAddressLine2',
    type: 'text',
    validation: Yup.string(),
    fullWidth: true,
  },
  {
    label: 'Previous City',
    placeholder: 'Type previous city',
    name: 'previousCity',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'Previous State',
    placeholder: 'Select previous state',
    name: 'previousState',
    type: 'select',
    options: US_STATES,
    validation: Yup.string(),
  },
  {
    label: 'Previous ZIP',
    placeholder: 'Type previous ZIP',
    name: 'previousZip',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'Previous Time At Address',
    placeholder: 'Type previous time at address',
    name: 'previousTimeAtAddress',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
  {
    label: 'Previous Own or Rent',
    placeholder: 'Select previous own or rent',
    name: 'previousOwnOrRent',
    type: 'select',
    options: [
      { value: 'Rent', label: 'Rent' },
      { value: 'Own', label: 'Own' },
    ],
    validtation: Yup.string(),
  },
  {
    label: 'Previous Rent Amount',
    placeholder: 'Type previous rent amount',
    name: 'previousRentAmount',
    type: 'currency',
    validation: Yup.string(),
  },
  {
    label: 'Mailing Addres',
    placeholder: 'Type mailing address',
    name: 'mailingAddres',
    type: 'text',
    validation: Yup.string(),
    fullWidth: true,
  },
];

export const employmentFields = [
  {
    label: 'Current Employment Employer',
    placeholder: 'Type employer',
    name: 'currentEmploymentEmployer',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label: 'Employment Phone',
    placeholder: 'Type employment phone',
    name: 'currentEmploymentPhone',
    type: 'phone',
    validation: validatePhone,
  },
  {
    label: 'Current Employment Address',
    placeholder: 'Type current employment address',
    name: 'currentEmploymentAddress',
    type: 'text',
    validation: Yup.string(),
    fullWidth: true,
  },
  {
    label: 'Position',
    placeholder: 'Type position',
    name: 'currentEmploymentPosition',
    type: 'text',
    validation: Yup.string(),
  },
  {
    label:
      'I am employed by a family member, property seller, real estate agent, or other party to the transaction',
    name: 'currentEmploymentRelation',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Start Date',
    placeholder: 'Select employment start date',
    name: 'currentEmploymentStartDate',
    type: 'date',
    validation: Yup.date(),
  },
  {
    label: 'Tenure (Months)',
    placeholder: 'Type tenure in months',
    name: 'currentEmploymentTenureMonths',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
  {
    label: 'Employment Owner',
    name: 'currentEmploymentOwner',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Ownership',
    placeholder: 'Select ownership',
    name: 'currentEmploymentOwnership',
    type: 'select',
    options: [
      { value: 'less', label: 'Less' },
      { value: 'more', label: 'More' },
    ],
    validation: Yup.string().oneOf(['less', 'more']),
  },
  {
    label: 'Monthly Income',
    placeholder: 'Type monthly income',
    name: 'currentEmploymentMonthlyIncome',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
    fullWidth: true,
  },
  {
    label: 'Base Gross Income',
    placeholder: 'Type base gross income',
    name: 'currentEmploymentGrossBase',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
  {
    label: 'Overtime Gross Income',
    placeholder: 'Type overtime gross income',
    name: 'currentEmploymentGrossOvertime',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
  {
    label: 'Bonus Gross Income',
    placeholder: 'Type bonus gross income',
    name: 'currentEmploymentGrossBonus',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
  {
    label: 'Commission Gross Income',
    placeholder: 'Type commission gross income',
    name: 'currentEmploymentGrossCommission',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
  {
    label: 'Military Gross Income',
    placeholder: 'Type military gross income',
    name: 'currentEmploymentGrossMilitary',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
  {
    label: 'Other Gross Income',
    placeholder: 'Type other gross income',
    name: 'currentEmploymentGrossOther',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },

  {
    label: 'Total Gross Income',
    placeholder: 'Type total gross income',
    name: 'currentEmploymentGrossTotal',
    type: 'number',
    validation: Yup.number().typeError('This field must be a number'),
  },
];

export const declarationFields = [
  {
    label: 'Will you occupy the property as your primary residence?',
    name: 'dec5a',
    type: 'checkbox',
    validation: Yup.boolean(),
    subFields: [
      {
        label:
          'Have you had an ownership interest in another property in the last three years?',
        name: 'dec5aYes',
        type: 'checkbox',
        validation: Yup.boolean(),
        row: true,
        subFields: [
          {
            label: 'What type of property did you own?',
            placeholder: 'Type response',
            name: 'dec5aYes1',
            type: 'text',
            validation: Yup.string(),
          },
          {
            label: 'How did you hold title to the property?',
            placeholder: 'Type response',
            name: 'dec5aYes2',
            type: 'text',
            validation: Yup.string(),
          },
        ],
      },
    ],
  },
  {
    label:
      'If this is a Purchase Transaction: Do you have a family relationship or business affiliation with the seller of the property?',
    name: 'dec5b',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label:
      'Are you borrowing any money for this real estate transaction (e.g. money for your closing costs or down payment) or obtaining any money from another party, such as the seller or realtor, that you have not disclosed on this loan application?',
    name: 'dec5c',
    type: 'checkbox',
    validation: Yup.boolean(),
    subFields: [
      {
        label: 'What is the amount of this money?',
        placeholder: 'Type amount',
        name: 'dec5cYes',
        type: 'currency',
        validation: Yup.string(),
      },
    ],
  },
  {
    label:
      'Have you or will you be applying for a mortgage loan on another property? (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application?',
    name: 'dec5d1',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label:
      'Have you or will you be applying for any new credit (e.g. installment loan, credit card, etc.) on or before closing this loan that is not disclosed on this application?',
    name: 'dec5d2',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label:
      'Will this property be subject to a lien that could take priority over the first mortgage lien, such as a clean energy lien paid through your property taxes (e.g. the Property Assessed Clean Energy Program)?',
    name: 'dec5e',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label:
      'Are you a co-signer or guarantor on any debt or loan that is not disclosed on this application?',
    name: 'dec5f',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Are there any outstanding judgments against you?',
    name: 'dec5g',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Are you currently delinquent or in default on a Federal debt?',
    name: 'dec5h',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label:
      'Are you a party to a lawsuit in which you potentially have any personal financial liability?',
    name: 'dec5i',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label:
      'Have you conveyed title to any property in lieu of foreclosure in the past 7 years?',
    name: 'dec5j',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label:
      'Within the past 7 years, have you completed a pre-foreclosure sale or short sale, whereby the property was sold to a third party and the Lender agreed to accept less than the outstanding mortgage balance due?',
    name: 'dec5k',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Have you had property foreclosed upon in the last 7 years?',
    name: 'dec5l',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'Have you declared bankruptcy within the past 7 years?',
    name: 'dec5m',
    type: 'checkbox',
    validation: Yup.boolean(),
    subFields: [
      {
        label: 'Identify the type(s) of bankruptcy',
        placeholder: 'Type response',
        name: 'dec5mYes',
        type: 'text',
        validation: Yup.string(),
        fullWidth: true,
      },
    ],
  },
  {
    label: 'Dec Military',
    name: 'decMilitary',
    type: 'checkbox',
    validation: Yup.boolean(),
    subFields: [
      {
        label: 'Dec Military',
        placeholder: 'Type military details',
        name: 'decMilitaryYes',
        type: 'text',
        validation: Yup.string(),
        fullWidth: true,
      },
    ],
  },
  {
    label: 'Ethnicity',
    type: 'section',
    fields: [
      {
        label: 'Hispanic or Latino',
        name: 'decH',
        type: 'checkbox',
        validation: Yup.boolean(),
        row: true,
        checkMarkAbsolute: true,
        subFields: [
          {
            label: 'Mexican',
            name: 'decMexican',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Puertorican',
            name: 'decPuertorican',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Cuban',
            name: 'decCuban',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Other Hispanic or Latino',
            name: 'decOtherHispanic',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
            checkMarkAbsolute: true,
            subFields: [
              {
                label: 'Other Hispanic or Latino Ethnicity',
                placeholder: 'Type details',
                name: 'decOtherHispanicTrue',
                type: 'text',
                validation: Yup.string(),
              },
            ],
          },
        ],
      },
      {
        label: 'Not Hispanic or Latino',
        name: 'decNotHispanic',
        type: 'checkbox',
        validation: Yup.boolean(),
        row: true,
      },
      {
        label: 'I do not wish to provide this information',
        name: 'decEthnicityNo',
        type: 'checkbox',
        validation: Yup.boolean(),
        row: true,
      },
    ],
  },
  {
    label: 'Race',
    type: 'section',
    fields: [
      {
        label: 'American Indian',
        name: 'decAmericanIndian',
        type: 'checkbox',
        validation: Yup.boolean(),
        row: true,
        checkMarkAbsolute: true,
        subFields: [
          {
            label: 'American Indian Tribe',
            placeholder: 'Type tribe',
            name: 'decAmericanIndianTribe',
            type: 'text',
            validation: Yup.string(),
          },
        ],
      },
      {
        label: 'Asian',
        name: 'decAsian',
        type: 'checkbox',
        validation: Yup.boolean(),
        row: true,
        checkMarkAbsolute: true,
        subFields: [
          {
            label: 'Asian Indian',
            name: 'decAsianIndian',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Chinese',
            name: 'decChinese',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Filipino',
            name: 'decFilipino',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Japanese',
            name: 'decJapanese',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Korean',
            name: 'decKorean',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Vietnamese',
            name: 'decVietnamese',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Other Asian',
            name: 'decOtherAsian',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
        ],
      },
      {
        label: 'Black or African American',
        name: 'decBlack',
        type: 'checkbox',
        validation: Yup.boolean(),
        row: true,
      },
      {
        label: 'Native Hawaiian or Other Pacific Islander',
        name: 'decNativeHawaiianOrOther',
        type: 'checkbox',
        validation: Yup.boolean(),
        row: true,
        checkMarkAbsolute: true,
        subFields: [
          {
            label: 'Native Hawaiian',
            name: 'decNativeHawaiian',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Guamanian or Chamorro',
            name: 'decGuamanian',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Samoan',
            name: 'decSamoan',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
          },
          {
            label: 'Other Pacific Islander',
            name: 'decOtherPacificIslander',
            type: 'checkbox',
            validation: Yup.boolean(),
            row: true,
            checkMarkAbsolute: true,
            subFields: [
              {
                label: 'Other Pacific Islander Ethnicity',
                placeholder: 'Type details',
                name: 'decOtherPacificIslanderYes',
                type: 'text',
                validation: Yup.string(),
              },
            ],
          },
        ],
      },
      {
        label: 'White',
        name: 'decWhite',
        type: 'checkbox',
        validation: Yup.boolean(),
        row: true,
      },
      {
        label: 'I do not wish to provide this information',
        name: 'decRaceNo',
        type: 'checkbox',
        validation: Yup.boolean(),
        row: true,
      },
    ],
  },

  {
    label:
      'Was the ethnicity of the Borrower collected on the basis of visual observation or surname?',
    name: 'decCollectedVisual',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label:
      'Was the sex of the Borrower collected on the basis of visual observation or surname?',
    name: 'decCollectedSex',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label:
      'Was the race of the Borrower collected on the basis of visual observation or surname?',
    name: 'decCollectedRace',
    type: 'checkbox',
    validation: Yup.boolean(),
  },
  {
    label: 'The Demographic Information was provided through:',
    placeholder: 'Please select',
    name: 'decDemoProvided',
    type: 'select',
    options: [
      {
        value:
          'Face-to-Face Interview (includes Electronic Media w/Video Component)',
        label:
          'Face-to-Face Interview (includes Electronic Media w/Video Component)',
      },
      {
        value: 'Telephone Interview',
        label: 'Telephone Interview',
      },
      {
        value: 'Fax or Mail',
        label: 'Fax or Mail',
      },
      {
        value: 'Email or Internet',
        label: 'Email or Internet',
      },
    ],
    validation: Yup.string(),
  },
];
